import { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Grid,
  InputAdornment,
  InputLabel,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useFormContext, Controller } from "react-hook-form";
import FormWrapper from "../../form-builder/components/common/FormWrapper";
import FormField from "../../form-builder/components/common/FormField";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import {
  selectAllCombinedRoles,
  // selectAllRoles,
} from "../../../bmr/slices/roles";
import { permissionValidationSchema } from "../../form-builder/utils/schemas/validation";
import { buildPermissionInitialValues } from "../../form-builder/utils/schemas/initial";
import { useLocation } from "react-router";

const FIELD_TYPE_MAP = {
  General: "text",
  Note: "textarea",
  Validations: "text",
  Label: "label",
  Toggle: "switch",
  "Input Radio": "radio-group",
  "Input Dropdown": "select",
  "Input Text": "text",
  "Input Number": "number",
  "Static Number": "number",
  "Input Email": "email",
  "Input Current Time": "time",
  "Input Date": "date",
  "Input Date and Time": "datetime",
  "Linked Field": "linked-field",
  Hyperlink: "hyperlink",
  "File Upload": "file",
  "Image Upload": "image_upload",
  Barcode: "barcode",
  "Input Checkbox": "checkbox-group",
};

const LAYOUT_OPTIONS = {
  12: { gridSize: 12 },
  6: { gridSize: 6 },
  4: { gridSize: 4 },
  3: { gridSize: 3 },
};

const processSections = (sections) =>
  sections?.map((section) => {
    const sorted = [...section.fields].sort((a, b) => a.order - b.order);
    const fieldGroups = [];
    let currentGroup = [];
    let currentLayout = null;

    sorted.forEach((field) => {
      const layout = field.property.field_layout || 12;
      if (currentLayout !== layout) {
        if (currentGroup.length)
          fieldGroups.push({ layout: currentLayout, fields: currentGroup });
        currentGroup = [];
        currentLayout = layout;
      }
      currentGroup.push({
        ...field,
        type: FIELD_TYPE_MAP[field.property.field_type] || "text",
        required: field.property.general?.required || false,
      });
    });

    if (currentGroup.length)
      fieldGroups.push({ layout: currentLayout, fields: currentGroup });

    return { ...section, fieldGroups };
  });

const buildFieldValueMap = (formData) => {
  const map = {};
  formData?.sections?.forEach((s) =>
    s.fields?.forEach((f) => {
      map[f.input_uuid] = f.value;
    }),
  );
  return map;
};

const getHiddenFields = (formData) => {
  const hidden = new Set();
  if (!formData?.sections?.length) return hidden;
  const allFields = formData.sections.flatMap((s) => s.fields || []);
  const fieldValueMap = buildFieldValueMap(formData);
  allFields
    .filter((f) => f.property?.conditions?.length)
    .forEach((fieldItem) => {
      const selectedValue = fieldValueMap[fieldItem.input_uuid];
      fieldItem.property.conditions.forEach((cond) => {
        if (cond.fbt_option_uuid !== selectedValue)
          hidden.add(cond.target_input_field_uuid);
      });
    });
  return hidden;
};

const FormGeneratorPermissionInner = ({
  formSections,
  filteredRolesOptions,
  allOptionsDict,
  fieldOptionsMap,
  hiddenFields,
  fieldValueMap,
  loading,
  allRoles,
  onSubmit,
}) => {
  const [expandedIndex, setExpandedIndex] = useState(0);
  const [sectionPermissions, setSectionPermissions] = useState({});
  const location = useLocation();

  const isViewMode = location.pathname.includes("view");
  useEffect(() => {
    if (!formSections?.length) return;
    const initial = {};
    formSections.forEach((section) => {
      const viewRoles =
        section?.persmissions?.view?.map((p) => p.role_id) || [];
      const fillRoles =
        section?.persmissions?.fill?.map((p) => p.role_id) || [];
      initial[section.section_uuid] = { view: viewRoles, fill: fillRoles };
    });
    setSectionPermissions(initial);
  }, [JSON.stringify(formSections)]);
  const {
    control,
    formState: { errors },
    setValue,
  } = useFormContext();

  const getRoleLabel = (roleId) =>
    allRoles?.find((r) => r.id === roleId)?.title || String(roleId);

  const extractSelectValue = (input) => {
    if (input?.target?.value !== undefined) return input.target.value;
    if (Array.isArray(input)) return input;
    return [];
  };

  const handlePermissionChange = (
    sectionUuid,
    type,
    rawInput,
    sectionIndex,
  ) => {
    const value = extractSelectValue(rawInput);
    setSectionPermissions((prev) => ({
      ...prev,
      [sectionUuid]: { ...prev[sectionUuid], [type]: value },
    }));
    setValue(`sections[${sectionIndex}].permissions[0].${type}`, value, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const handleAccordionChange = (index) => (_event, isExpanded) =>
    setExpandedIndex(isExpanded ? index : null);

  const handleFieldChange = (fieldUuid, value) => {
    console.log(fieldUuid, value, "fieldUuid, value");
  };

  const handleDateChange = (field, rawValue) => {
    switch (field.property.field_type) {
      case "Input Date":
        return rawValue ? dayjs(rawValue).format("YYYY-MM-DD") : "";
      case "Input Current Time":
        return rawValue || "";
      case "Input Date and Time":
        return rawValue ? dayjs(rawValue).toISOString() : "";
      default:
        return rawValue;
    }
  };

  const resolveChangeValue = (field, input) => {
    if (typeof input === "object" && input !== null && input.$d)
      return handleDateChange(field, input);
    if (input?.target) return handleDateChange(field, input.target.value);
    return handleDateChange(field, input);
  };

  const handleSubmit = () => {
    const sectionsPayload = formSections.map((section) => {
      const viewRoleIds = sectionPermissions[section.section_uuid]?.view || [];
      const fillRoleIds = sectionPermissions[section.section_uuid]?.fill || [];
      return {
        view: { role_id: viewRoleIds },
        fill: { role_id: fillRoleIds },
        section_uuid: section.section_uuid,
      };
    });
    onSubmit(sectionsPayload);
  };

  const hasAllPermissions = formSections.every((section) => {
    const perms = sectionPermissions[section.section_uuid];
    return perms?.view?.length > 0 && perms?.fill?.length > 0;
  });
  const isSaveDisabled = loading || !hasAllPermissions;

  return (
    <>
      {formSections.map((section, index) => {
        const isExpanded = expandedIndex === index;

        // Original derived values — unchanged
        const currentViewRoles =
          sectionPermissions[section.section_uuid]?.view || [];
        const currentFillRoles =
          sectionPermissions[section.section_uuid]?.fill || [];
        const sectionErrors = errors?.sections?.[index]?.permissions?.[0];
        const viewError = sectionErrors?.view?.message;
        const fillError = sectionErrors?.fill?.message;

        return (
          <Box
            key={section.section_uuid}
            sx={{
              border: `1px solid ${isExpanded ? "#1c5fdaff" : "#E0E0E0"}`,
              borderRadius: "8px",
              mb: 2,
              overflow: "hidden",
            }}
          >
            {/* ── Section header (unchanged) ── */}
            <Box className="w-full border border-gray-200 rounded-md bg-white">
              <Box className="p-3 border-b border-gray-200">
                <Typography
                  variant="subtitle1"
                  sx={{ fontSize: "1rem" }}
                  className="font-semibold text-blue-600"
                >
                  {section.name}
                </Typography>
                {section.description && (
                  <Typography
                    variant="body2"
                    className="text-gray-500 mt-1"
                    sx={{ fontSize: "0.875rem" }}
                  >
                    {section.description}
                  </Typography>
                )}
              </Box>

              {/* ── Permissions ── */}
              <Box className="px-6 py-6 space-y-6">
                {/* ── View Permission ── */}
                <Box className="grid grid-cols-12 items-center gap-4">
                  <InputLabel
                    required
                    error={!!viewError}
                    className="col-span-2 font-bold text-gray-800"
                  >
                    View Permission
                  </InputLabel>
                  <Box className="col-span-10">
                    <Controller
                      name={`sections[${index}].permissions[0].view`}
                      control={control}
                      render={({ field: rhfField }) => (
                        <FormField
                          name={`view_${section.section_uuid}`}
                          type="select"
                          size="small"
                          fullWidth
                          multiple
                          placeholder="Select roles"
                          options={filteredRolesOptions}
                          value={currentViewRoles}
                          error={!!viewError}
                          helperText={viewError}
                          disabled={isViewMode}
                          onChange={(input) =>
                            handlePermissionChange(
                              section.section_uuid,
                              "view",
                              input,
                              index,
                            )
                          }
                          onBlur={rhfField.onBlur}
                          renderValue={(selected) => {
                            if (!selected?.length)
                              return (
                                <em style={{ color: "#9CA3AF" }}>
                                  Select roles
                                </em>
                              );
                            return (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 0.5,
                                }}
                              >
                                {selected.map((roleId) => (
                                  <Chip
                                    key={roleId}
                                    label={getRoleLabel(roleId)}
                                    size="small"
                                    sx={{
                                      backgroundColor: "#EEF4FF",
                                      color: "#3571FF",
                                    }}
                                  />
                                ))}
                              </Box>
                            );
                          }}
                        />
                      )}
                    />
                    {isViewMode && viewError && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 0.5, display: "block" }}
                      >
                        {viewError}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* ── Fill Permission ── */}
                <Box className="grid grid-cols-12 items-center gap-4">
                  <InputLabel
                    required
                    error={!!fillError}
                    className="col-span-2 font-bold text-gray-800"
                  >
                    Form Fill Permission
                  </InputLabel>
                  <Box className="col-span-10">
                    <Controller
                      name={`sections[${index}].permissions[0].fill`}
                      control={control}
                      render={({ field: rhfField }) => (
                        <FormField
                          name={`fill_${section.section_uuid}`}
                          type="select"
                          size="small"
                          fullWidth
                          multiple
                          placeholder="Select roles"
                          options={filteredRolesOptions}
                          value={currentFillRoles}
                          error={!!fillError}
                          helperText={fillError}
                          disabled={isViewMode}
                          onChange={(input) =>
                            handlePermissionChange(
                              section.section_uuid,
                              "fill",
                              input,
                              index,
                            )
                          }
                          onBlur={rhfField.onBlur}
                          renderValue={(selected) => {
                            if (!selected?.length)
                              return (
                                <em style={{ color: "#9CA3AF" }}>
                                  Select roles
                                </em>
                              );
                            return (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 0.5,
                                }}
                              >
                                {selected.map((roleId) => (
                                  <Chip
                                    key={roleId}
                                    label={getRoleLabel(roleId)}
                                    size="small"
                                    sx={{
                                      backgroundColor: "#F0FDF4",
                                      color: "#22C55E",
                                    }}
                                  />
                                ))}
                              </Box>
                            );
                          }}
                        />
                      )}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>

            <Accordion
              expanded={isExpanded}
              onChange={handleAccordionChange(index)}
              elevation={0}
              sx={{
                border: "none",
                margin: 0,
                "&:before": { display: "none" },
                "&.Mui-expanded": { margin: 0 },
              }}
            >
              {(section.property?.display_details ||
                section.property?.display_field_number) && (
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    borderBottom: "1px solid #E0E0E0",
                    background: "#EEF4FF",
                    minHeight: "64px",
                    "& .MuiAccordionSummary-content": {
                      margin: 0,
                      display: "block",
                      padding: "0.5rem",
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600, color: "#3571FF" }}
                  >
                    {isExpanded ? "Hide Input Fields" : "Show Input Fields"}
                  </Typography>
                </AccordionSummary>
              )}

              <AccordionDetails className="p-0 px-2">
                {section.fieldGroups.map((group, groupIndex) => (
                  <Grid key={groupIndex} container>
                    {group.fields.map((field) => {
                      if (hiddenFields.has(field.input_uuid)) return null;

                      const fieldValue =
                        fieldValueMap[field.input_uuid] ?? field.value ?? "";

                      const calcProp = field?.property?.calculation;
                      const showFormula = calcProp?.display_formula
                        ? JSON.parse(calcProp.display_formula)
                        : false;

                      const isCalculation =
                        field.property.field_type === "Calculation";
                      const isLinkedField =
                        field.property.field_type === "Linked Field";
                      const isLabel = field.type === "label";

                      return (
                        <Grid
                          item
                          key={field.input_uuid}
                          xs={12}
                          sm={LAYOUT_OPTIONS[group.layout]?.gridSize || 12}
                        >
                          <Box sx={{ padding: 1, paddingTop: "1rem" }}>
                            {isLabel ? (
                              <Typography
                                variant="body2"
                                className="text-sm font-bold"
                              >
                                {field.label}
                              </Typography>
                            ) : (
                              <Typography
                                variant="subtitle1"
                                className="inline-block font-normal"
                                sx={{ paddingBottom: "0.625rem" }}
                              >
                                {field.label}
                                {calcProp?.unit_of_measurement
                                  ? ` (${calcProp.unit_of_measurement})`
                                  : ""}
                                {field.property?.general?.required && (
                                  <Box
                                    component="span"
                                    className="text-red-500"
                                    sx={{ ml: 0.5 }}
                                  >
                                    *
                                  </Box>
                                )}
                              </Typography>
                            )}

                            {isCalculation ? (
                              <>
                                <FormField
                                  name={field.input_uuid}
                                  type="number"
                                  label=""
                                  fullWidth
                                  value={fieldValue}
                                  disabled
                                  size="small"
                                />
                                {showFormula && calcProp?.formula && (
                                  <Typography variant="caption">
                                    Formula: {field.parsedFormula}
                                  </Typography>
                                )}
                              </>
                            ) : isLinkedField ? (
                              <FormField
                                name={field.input_uuid}
                                label=""
                                fullWidth
                                value={
                                  allOptionsDict[fieldValue] ||
                                  fieldValue ||
                                  field.property.linked_field?.linked_field ||
                                  ""
                                }
                                disabled
                                size="small"
                              />
                            ) : (
                              <FormField
                                name={`sections.${index}.fields.${0}.${
                                  field.label
                                }`}
                                type={field.type}
                                options={
                                  [
                                    "radio-group",
                                    "select",
                                    "checkbox-group",
                                  ].includes(field.type)
                                    ? fieldOptionsMap[field.input_uuid] || []
                                    : []
                                }
                                row="row"
                                noMargin
                                disabled
                                placeholder={
                                  field.placeholder ||
                                  `Enter ${
                                    field.label?.toLowerCase() || "value"
                                  }`
                                }
                                hasBothLabels={field.type === "Toggle"}
                                label1={field.property?.toggle?.toggle_msg_1}
                                label2={field.property?.toggle?.toggle_msg_2}
                                onChange={(input) =>
                                  handleFieldChange(
                                    field.input_uuid,
                                    resolveChangeValue(field, input),
                                  )
                                }
                                format={
                                  field.property.field_type === "Input Date"
                                    ? field.property?.input_date
                                        ?.date_display_format
                                    : "DD/MM/YY"
                                }
                                timeFormat={
                                  field.property.field_type ===
                                  "Input Current Time"
                                    ? field.property?.input_current_time
                                        ?.time_format
                                    : "hh:mm A"
                                }
                                combineDate={
                                  field.property.field_type ===
                                  "Input Date and Time"
                                    ? field.property?.input_date_and_time
                                        ?.date_display_format
                                    : "DD/MM/YY"
                                }
                                combineTime={
                                  field.property.field_type ===
                                  "Input Date and Time"
                                    ? field.property?.input_date_and_time
                                        ?.time_format
                                    : "HH:mm"
                                }
                                linkLabel={field.property?.hyperlink?.name}
                                linkUrl={field.property?.hyperlink?.url}
                                defaultValue={
                                  field.property.field_type === "Input Text"
                                    ? field.property?.input_text?.default_val
                                    : ""
                                }
                                value={fieldValue}
                                endAdornment={
                                  field.type === "number" &&
                                  field?.property?.input_number
                                    ?.measurement_unit ? (
                                    <InputAdornment position="end">
                                      <Typography variant="body2">
                                        {
                                          field.property.input_number
                                            .measurement_unit
                                        }
                                      </Typography>
                                    </InputAdornment>
                                  ) : null
                                }
                              />
                            )}
                          </Box>

                          {field.property?.note?.message_note && (
                            <Typography
                              variant="body2"
                              className="px-2"
                              sx={{ mb: 1, color: "#FB9D2C" }}
                            >
                              Note: {field.property.note.message_note}
                            </Typography>
                          )}
                        </Grid>
                      );
                    })}
                  </Grid>
                ))}
              </AccordionDetails>
            </Accordion>
          </Box>
        );
      })}
      {!isViewMode && (
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isSaveDisabled}
            sx={{ borderRadius: "0.375rem", width: "7.18rem" }}
          >
            Save
          </Button>
        </Box>
      )}
    </>
  );
};

// ── Outer component ───────────────────────────────────────────────────────────

const FormGeneratorPermission = ({ formJson, onSubmit, loading, formData }) => {
  const { selectedRolesFormatted } = useSelector(
    (state) => state?.assignedTemplates,
  );
  const allRoles = useSelector(selectAllCombinedRoles);

  const formSections = useMemo(() => processSections(formJson), [formJson]);

  const filteredRolesOptions = useMemo(() => {
    const roleIds = new Set(selectedRolesFormatted?.map((r) => r.value) || []);
    return (
      allRoles
        ?.filter((role) => roleIds.has(role.id))
        ?.map((role) => ({ value: role.id, label: role.title })) || []
    );
  }, [selectedRolesFormatted, allRoles]);

  const allOptionsDict = useMemo(() => {
    const dict = {};
    formJson?.forEach((section) =>
      section.fields?.forEach((field) =>
        field?.property?.options?.options?.forEach((opt) => {
          dict[opt.option_uuid] = opt.label;
        }),
      ),
    );
    return dict;
  }, [formJson]);

  const fieldOptionsMap = useMemo(() => {
    const map = {};
    formJson?.forEach((section) =>
      section.fields?.forEach((field) => {
        const opts = field?.property?.options?.options || [];
        map[field.input_uuid] = opts.map((opt) => ({
          value: opt.option_uuid,
          label: opt.label,
        }));
      }),
    );
    return map;
  }, [formJson]);

  const hiddenFields = useMemo(() => getHiddenFields(formData), [formData]);
  const fieldValueMap = useMemo(() => buildFieldValueMap(formData), [formData]);
  const sectionUUIDKey = JSON.stringify(
    formSections?.map((s) => s.section_uuid),
  );

  const defaultValues = useMemo(
    () => buildPermissionInitialValues(formSections),
    [sectionUUIDKey],
  );

  if (!formSections?.length) return null;

  return (
    <FormWrapper
      onSubmit={() => {}}
      defaultValues={defaultValues}
      validationSchema={permissionValidationSchema}
      loading={loading}
      mode="onChange"
      className="p-3"
    >
      <FormGeneratorPermissionInner
        formSections={formSections}
        filteredRolesOptions={filteredRolesOptions}
        allOptionsDict={allOptionsDict}
        fieldOptionsMap={fieldOptionsMap}
        hiddenFields={hiddenFields}
        fieldValueMap={fieldValueMap}
        loading={loading}
        allRoles={allRoles}
        onSubmit={onSubmit}
      />
    </FormWrapper>
  );
};

export default FormGeneratorPermission;
